import { CaretDown, CaretRight, Clock, Eye, Calculator } from "@phosphor-icons/react";
import type { ExtractedStandardFields } from "../types";
import { AIDetailsPanel } from "./AIDetailsPanel";

interface StandardFieldsPanelProps {
  fileId: string;
  fields: ExtractedStandardFields;
  isExpanded: boolean;
  expandedAIDetails: Set<string>;
  onToggleExpand: (fileId: string) => void;
  onToggleAIDetails: (key: string) => void;
}

export function StandardFieldsPanel({
  fileId,
  fields,
  isExpanded,
  expandedAIDetails,
  onToggleExpand,
  onToggleAIDetails,
}: StandardFieldsPanelProps) {
  const hasAnyValue = Object.keys(fields).some(
    (key) => fields[key as keyof ExtractedStandardFields]?.value
  );

  if (!hasAnyValue) return null;

  return (
    <div className="mt-2 pt-2 border-t border-neutral-100">
      <button
        onClick={() => onToggleExpand(fileId)}
        className="flex items-center gap-1.5 text-xs font-medium text-blue-700 hover:text-blue-800 transition-colors"
      >
        {isExpanded ? (
          <CaretDown className="h-3.5 w-3.5" weight="bold" />
        ) : (
          <CaretRight className="h-3.5 w-3.5" weight="bold" />
        )}
        <Clock className="h-3.5 w-3.5" weight="duotone" />
        <span>Geëxtraheerde velden</span>
      </button>

      {isExpanded && (
        <div className="mt-2 space-y-2 bg-blue-50/50 rounded-md p-2 border border-blue-100">
          {/* Document Date */}
          {fields.documentDate?.value && (
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-medium text-blue-800">Documentdatum:</span>
                <span className="text-xs text-neutral-700">
                  {new Date(fields.documentDate.value).toLocaleDateString('nl-BE')}
                </span>
                {fields.documentDate.aiDetails && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleAIDetails(`${fileId}-documentDate`);
                    }}
                    className="ml-auto text-[9px] text-blue-600 hover:text-blue-700 flex items-center gap-0.5"
                  >
                    <Eye className="h-3 w-3" />
                    {expandedAIDetails.has(`${fileId}-documentDate`) ? 'Verberg' : 'AI details'}
                  </button>
                )}
              </div>
              {fields.documentDate.reasoning && (
                <p className="text-[10px] text-neutral-500 italic pl-2 border-l-2 border-blue-200">
                  💡 {fields.documentDate.reasoning}
                </p>
              )}
              {expandedAIDetails.has(`${fileId}-documentDate`) && fields.documentDate.aiDetails && (
                <AIDetailsPanel aiDetails={fields.documentDate.aiDetails} variant="field" />
              )}
            </div>
          )}

          {/* Expiration Date */}
          {fields.expirationDate?.value && (
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-medium text-blue-800">Vervaldatum:</span>
                <span className="text-xs text-neutral-700">
                  {new Date(fields.expirationDate.value).toLocaleDateString('nl-BE')}
                </span>
                {fields.expirationDate.aiDetails?.calculationApplied && (
                  <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] bg-emerald-100 text-emerald-700">
                    <Calculator className="h-2.5 w-2.5" />
                    Berekend
                  </span>
                )}
                {fields.expirationDate.aiDetails && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleAIDetails(`${fileId}-expirationDate`);
                    }}
                    className="ml-auto text-[9px] text-blue-600 hover:text-blue-700 flex items-center gap-0.5"
                  >
                    <Eye className="h-3 w-3" />
                    {expandedAIDetails.has(`${fileId}-expirationDate`) ? 'Verberg' : 'AI details'}
                  </button>
                )}
              </div>
              {fields.expirationDate.reasoning && (
                <p className="text-[10px] text-neutral-500 italic pl-2 border-l-2 border-blue-200">
                  💡 {fields.expirationDate.reasoning}
                </p>
              )}
              {expandedAIDetails.has(`${fileId}-expirationDate`) && fields.expirationDate.aiDetails && (
                <AIDetailsPanel
                  aiDetails={fields.expirationDate.aiDetails}
                  variant="field"
                  calculationApplied={fields.expirationDate.aiDetails.calculationApplied}
                />
              )}
            </div>
          )}

          {/* Sign Status */}
          {fields.signStatus?.value && (
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-medium text-blue-800">Handtekening status:</span>
                <span className="text-xs text-neutral-700">
                  {fields.signStatus.value === 'signed' ? 'Getekend' :
                   fields.signStatus.value === 'draft' ? 'Concept' :
                   fields.signStatus.value === 'not_applicable' ? 'N.v.t.' :
                   'Onbekend'}
                </span>
                {fields.signStatus.aiDetails && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleAIDetails(`${fileId}-signStatus`);
                    }}
                    className="ml-auto text-[9px] text-blue-600 hover:text-blue-700 flex items-center gap-0.5"
                  >
                    <Eye className="h-3 w-3" />
                    {expandedAIDetails.has(`${fileId}-signStatus`) ? 'Verberg' : 'AI details'}
                  </button>
                )}
              </div>
              {fields.signStatus.reasoning && (
                <p className="text-[10px] text-neutral-500 italic pl-2 border-l-2 border-blue-200">
                  💡 {fields.signStatus.reasoning}
                </p>
              )}
              {expandedAIDetails.has(`${fileId}-signStatus`) && fields.signStatus.aiDetails && (
                <AIDetailsPanel aiDetails={fields.signStatus.aiDetails} variant="field" />
              )}
            </div>
          )}

          {/* Summary reasoning */}
          {fields.summary?.reasoning && (
            <div className="space-y-0.5">
              <span className="text-[10px] font-medium text-blue-800">Samenvatting onderbouwing:</span>
              <p className="text-[10px] text-neutral-500 italic pl-2 border-l-2 border-blue-200">
                💡 {fields.summary.reasoning}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
